import { useEffect, useState, type FormEvent } from 'react';
import { Ban, Copy, Gamepad2, Plus, Ticket } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { GameRedeemCode, RedeemCode } from '../types';
import { currencyInputToCents, formatMoney, formatTime } from '../utils';
import { Badge, Button, CollapsiblePanel, EmptyBlock, Field, LoadingBlock, PageHeader, Panel, TextInput } from '../components/ui';

export function RedeemPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [codes, setCodes] = useState<RedeemCode[] | null>(null);
  const [createdCode, setCreatedCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [gameCodes, setGameCodes] = useState<GameRedeemCode[] | null>(null);
  const [createdGameCode, setCreatedGameCode] = useState('');
  const [gameBusy, setGameBusy] = useState(false);
  const [gameRewardType, setGameRewardType] = useState('item');

  const load = async () => {
    if (!permissions.canManageRedeemCodes) return;
    const data = await api<{ codes: RedeemCode[] }>('/api/admin/redeem-codes');
    setCodes(data.codes);
  };

  const loadGameCodes = async () => {
    if (!permissions.isSuperadmin) return;
    const data = await api<{ codes: GameRedeemCode[] }>('/api/admin/game-redeem-codes');
    setGameCodes(data.codes);
  };

  useEffect(() => {
    void load().catch(() => toast('兑换码列表加载失败', 'error'));
    void loadGameCodes().catch(() => toast('游戏兑换码列表加载失败', 'error'));
  }, [permissions.canManageRedeemCodes, permissions.isSuperadmin, user?.id]);

  const redeem = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return onNeedAuth();
    if (!user.boundToGame) return toast('请先绑定游戏角色后再兑换', 'error');
    try {
      const data = await api<{ amountCents: number }>('/api/redeem', { method: 'POST', body: JSON.stringify({ code: redeemCode }) });
      setRedeemCode('');
      await refresh();
      toast('兑换成功，到账 ' + formatMoney(data.amountCents, settings.currency), 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '兑换失败', 'error');
    }
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const expires = String(form.get('expiresAt') || '');
    setBusy(true);
    try {
      const data = await api<{ code: string }>('/api/admin/redeem-codes', {
        method: 'POST',
        body: JSON.stringify({
          amountCents: currencyInputToCents(String(form.get('amount') || '0')),
          maxUses: Number(form.get('maxUses') || 1),
          expiresAt: expires ? new Date(expires).getTime() : null
        })
      });
      setCreatedCode(data.code);
      formEl.reset();
      await load();
      toast('兑换码已创建', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '创建失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const createGame = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const expires = String(form.get('expiresAt') || '');
    setGameBusy(true);
    try {
      const data = await api<{ code: string }>('/api/admin/game-redeem-codes', {
        method: 'POST',
        body: JSON.stringify({
          rewardLabel: String(form.get('rewardLabel') || ''),
          rewardType: String(form.get('rewardType') || 'item'),
          itemId: String(form.get('itemId') || ''),
          count: Number(form.get('count') || 1),
          objective: String(form.get('objective') || '金币'),
          score: Number(form.get('score') || 0),
          command: String(form.get('command') || ''),
          maxUses: Number(form.get('maxUses') || 1),
          expiresAt: expires ? new Date(expires).getTime() : null
        })
      });
      setCreatedGameCode(data.code);
      formEl.reset();
      setGameRewardType('item');
      await loadGameCodes();
      toast('游戏兑换码已创建', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '创建游戏兑换码失败', 'error');
    } finally {
      setGameBusy(false);
    }
  };

  const disableGame = async (code: GameRedeemCode) => {
    const approved = await confirm({ title: '停用游戏兑换码', body: '停用后玩家将不能再在游戏内使用，已领取的记录不受影响。', danger: true, confirmText: '确认停用' });
    if (!approved) return;
    try {
      await api('/api/admin/game-redeem-codes/' + code.id + '/disable', { method: 'PATCH' });
      await loadGameCodes();
      toast('游戏兑换码已停用', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '停用失败', 'error');
    }
  };

  const disable = async (code: RedeemCode) => {
    const approved = await confirm({ title: '停用兑换码', body: '停用后不能再被兑换，已兑换记录不受影响。', danger: true, confirmText: '确认停用' });
    if (!approved) return;
    try {
      await api('/api/admin/redeem-codes/' + code.id + '/disable', { method: 'PATCH' });
      await load();
      toast('兑换码已停用', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '停用失败', 'error');
    }
  };

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast('已复制到剪贴板', 'success');
  };

  return (
    <>
      <PageHeader title="兑换码" description="管理员可创建网站兑换码；超级管理员可额外创建游戏内直接使用的免绑定兑换码。" />
      <div className="two-column">
        <Panel title="使用兑换码" eyebrow="REDEEM">
          <form className="stack-form" onSubmit={redeem}>
            <Field label="输入兑换码"><TextInput value={redeemCode} onChange={event => setRedeemCode(event.target.value.toUpperCase())} placeholder="MC-XXXXXXXXXXXX" required /></Field>
            <Button variant="primary" type="submit"><Ticket size={17} /> 立即兑换</Button>
          </form>
          {createdCode && (
            <div className="created-code">
              <span>新兑换码只显示这一次，请立即保存：</span>
              <code>{createdCode}</code>
              <Button variant="ghost" onClick={() => void copy(createdCode)}><Copy size={15} /> 复制</Button>
            </div>
          )}
        </Panel>

        {permissions.canManageRedeemCodes && (
          <Panel title="创建兑换码" eyebrow="ISSUE">
            <form className="stack-form" onSubmit={create}>
              <div className="form-grid">
                <Field label={'面额（' + settings.currency + '）'}><TextInput name="amount" type="number" min="0.01" step="0.01" required /></Field>
                <Field label="最大使用次数"><TextInput name="maxUses" type="number" min="1" max="10000" defaultValue="1" required /></Field>
              </div>
              <Field label="过期时间（可选）"><TextInput name="expiresAt" type="datetime-local" /></Field>
              <Button variant="primary" type="submit" loading={busy}><Plus size={17} /> 生成兑换码</Button>
            </form>
          </Panel>
        )}
      </div>

      {permissions.canManageRedeemCodes && (
        <CollapsiblePanel title="兑换码记录" eyebrow="ISSUE LOG" className="top-gap" defaultOpen={false} action={<span className="muted small">{codes === null ? '加载中' : codes.length + ' 条'}</span>}>
          {!codes ? <LoadingBlock /> : codes.length === 0 ? <EmptyBlock title="还没有创建兑换码" /> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>前缀</th><th>面额</th><th>使用情况</th><th>过期时间</th><th>创建人</th><th>状态</th><th>操作</th></tr></thead>
                <tbody>
                  {codes.map(code => (
                    <tr key={code.id}>
                      <td><code>{code.codePrefix}****</code></td>
                      <td>{formatMoney(code.amountCents, settings.currency)}</td>
                      <td>{code.usedCount} / {code.maxUses}</td>
                      <td>{code.expiresAt ? formatTime(code.expiresAt) : '长期有效'}</td>
                      <td>{code.createdByName}</td>
                      <td><Badge tone={code.disabledAt ? 'danger' : code.expiresAt && code.expiresAt < Date.now() ? 'muted' : 'good'}>{code.disabledAt ? '已停用' : code.expiresAt && code.expiresAt < Date.now() ? '已过期' : '可用'}</Badge></td>
                      <td>{!code.disabledAt && <Button variant="danger" onClick={() => void disable(code)}><Ban size={15} /> 停用</Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CollapsiblePanel>
      )}

      {permissions.isSuperadmin && (
        <CollapsiblePanel title="游戏免绑定兑换码" eyebrow="GAME CODE" className="top-gap" defaultOpen={false} action={<span className="muted small">{gameCodes === null ? '加载中' : gameCodes.length + ' 条'}</span>}>
          <p className="muted small">玩家在游戏聊天栏直接输入兑换码领取奖励，不需要登录网站或绑定游戏账号。仅超级管理员可以创建和停用。</p>
          <form className="stack-form" onSubmit={createGame}>
            <div className="form-grid">
              <Field label="奖励说明" hint="例如：100钻石礼包、金币 +5000"><TextInput name="rewardLabel" required /></Field>
              <Field label="奖励类型"><select className="input" name="rewardType" value={gameRewardType} onChange={event => setGameRewardType(event.target.value)}><option value="item">游戏物品</option><option value="score">计分板分数</option><option value="command">自定义命令</option></select></Field>
            </div>
            {gameRewardType === 'item' && <div className="form-grid"><Field label="物品名称 / ID" hint="支持 diamond、钻石 等常见名称"><TextInput name="itemId" placeholder="钻石 / diamond" required /></Field><Field label="数量"><TextInput name="count" type="number" min="1" defaultValue="1" required /></Field></div>}
            {gameRewardType === 'score' && <div className="form-grid"><Field label="计分板名称"><TextInput name="objective" defaultValue="金币" required /></Field><Field label="增加分数"><TextInput name="score" type="number" defaultValue="1000" required /></Field></div>}
            {gameRewardType === 'command' && <Field label="执行命令" hint="使用 {player} 代表玩家名"><TextInput name="command" placeholder="give {player} diamond 1" required /></Field>}
            <div className="form-grid"><Field label="最多使用次数"><TextInput name="maxUses" type="number" min="1" max="10000" defaultValue="1" required /></Field><Field label="过期时间（可选）"><TextInput name="expiresAt" type="datetime-local" /></Field></div>
            <Button variant="primary" type="submit" loading={gameBusy}><Gamepad2 size={17} /> 生成游戏兑换码</Button>
          </form>
          {createdGameCode && <div className="created-code"><span>游戏兑换码只显示这一次，请立即保存：</span><code>{createdGameCode}</code><Button variant="ghost" onClick={() => void copy(createdGameCode)}><Copy size={15} /> 复制</Button></div>}
          <div className="top-gap">
            {!gameCodes ? <LoadingBlock /> : gameCodes.length === 0 ? <EmptyBlock title="还没有创建游戏兑换码" /> : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>前缀</th><th>奖励</th><th>使用情况</th><th>过期时间</th><th>创建人</th><th>状态</th><th>操作</th></tr></thead>
                  <tbody>
                    {gameCodes.map(code => (
                      <tr key={code.id}>
                        <td><code>{code.codePrefix}****</code></td>
                        <td>{code.rewardLabel}</td>
                        <td>{code.usedCount} / {code.maxUses}</td>
                        <td>{code.expiresAt ? formatTime(code.expiresAt) : '长期有效'}</td>
                        <td>{code.createdByName}</td>
                        <td><Badge tone={code.disabledAt ? 'danger' : code.usedCount >= code.maxUses ? 'muted' : 'good'}>{code.disabledAt ? '已停用' : code.usedCount >= code.maxUses ? '已领完' : '可用'}</Badge></td>
                        <td>{!code.disabledAt && <Button variant="danger" onClick={() => void disableGame(code)}><Ban size={15} /> 停用</Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CollapsiblePanel>
      )}
    </>
  );
}
